import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useApi } from "../../hooks/useApi";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
} from "recharts";
import {
    FolderKanban,
    Users,
    Receipt,
    TrendingUp,
    ArrowUpRight,
    AlertCircle,
    Clock,
    CheckCircle2,
    Plus,
} from "lucide-react";
import { formatCurrency, getStatusColor } from "../../lib/utils";
import { AnalyticsSection } from "../../components/dashboard/AnalyticsSection";
import CurrencyConverterWidget from "../../components/dashboard/CurrencyConverterWidget";

const COLORS = ["#1E3A8A", "#F97316", "#10B981", "#F59E0B", "#EF4444"];

export const DashboardPage = () => {
    const navigate = useNavigate();
    const { get } = useApi();
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [recentProjects, setRecentProjects] = useState([]);
    const [revenueData, setRevenueData] = useState([]);
    const [projectStatusData, setProjectStatusData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            const results = await Promise.allSettled([
                get("/dashboard/stats"),
                get("/dashboard/recent-projects"),
                get("/dashboard/revenue-chart"),
                get("/dashboard/project-status-chart"),
            ]);
            if (results[0].status === "fulfilled") setStats(results[0].value);
            if (results[1].status === "fulfilled") setRecentProjects(results[1].value);
            if (results[2].status === "fulfilled") setRevenueData(results[2].value);
            if (results[3].status === "fulfilled") {
                setProjectStatusData(results[3].value.filter((s) => s.count > 0));
            }
            setLoading(false);
        };

        fetchData();
    }, [get]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    const kpiCards = [
        {
            title: t("dashboard.activeProjects"),
            value: stats?.active_projects || 0,
            total: stats?.total_projects || 0,
            icon: FolderKanban,
            color: "text-blue-600",
            bgColor: "bg-blue-100 dark:bg-blue-900/30",
        },
        {
            title: t("clients.title"),
            value: stats?.total_clients || 0,
            icon: Users,
            color: "text-green-600",
            bgColor: "bg-green-100 dark:bg-green-900/30",
        },
        {
            title: t("dashboard.totalRevenue"),
            value: formatCurrency(stats?.total_revenue || 0),
            icon: TrendingUp,
            color: "text-primary",
            bgColor: "bg-primary/10",
        },
        {
            title: t("dashboard.unpaidInvoices"),
            value: formatCurrency(stats?.pending_invoices || 0),
            subValue: stats?.overdue_invoices > 0 ? `${formatCurrency(stats.overdue_invoices)} ${t("status.overdue").toLowerCase()}` : null,
            icon: Receipt,
            color: "text-orange-600",
            bgColor: "bg-orange-100 dark:bg-orange-900/30",
        },
    ];

    return (
        <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">{t("dashboard.title")}</h1>
                    <p className="text-muted-foreground mt-1">{t("dashboard.subtitle")}</p>
                </div>
                <Button onClick={() => navigate("/projects/new")} data-testid="new-project-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    {t("projects.addProject")}
                </Button>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {kpiCards.map((kpi, index) => {
                    const Icon = kpi.icon;
                    return (
                        <Card
                            key={index}
                            className="hover:shadow-md transition-shadow duration-300"
                            data-testid={`kpi-card-${index}`}
                        >
                            <CardContent className="p-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-sm text-muted-foreground">{kpi.title}</p>
                                        <p className="font-heading font-bold text-2xl mt-1">{kpi.value}</p>
                                        {kpi.total && (
                                            <p className="text-xs text-muted-foreground mt-1">
                                                {t("common.all").toLowerCase()} {kpi.total}
                                            </p>
                                        )}
                                        {kpi.subValue && (
                                            <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                                                <AlertCircle className="w-3 h-3" />
                                                {kpi.subValue}
                                            </p>
                                        )}
                                    </div>
                                    <div className={`p-3 rounded-xl ${kpi.bgColor}`}>
                                        <Icon className={`w-5 h-5 ${kpi.color}`} />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Revenue Chart */}
                <Card className="lg:col-span-8" data-testid="revenue-chart">
                    <CardHeader>
                        <CardTitle className="font-heading text-lg">{t("dashboard.totalRevenue")}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-80">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={revenueData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                                    <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                                    <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: "hsl(var(--card))",
                                            border: "1px solid hsl(var(--border))",
                                            borderRadius: "8px",
                                        }}
                                        formatter={(value) => [formatCurrency(value), t("dashboard.totalRevenue")]}
                                    />
                                    <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>

                {/* Project Status Pie Chart */}
                <Card className="lg:col-span-4" data-testid="project-status-chart">
                    <CardHeader>
                        <CardTitle className="font-heading text-lg">{t("common.status")}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={projectStatusData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={80}
                                        paddingAngle={5}
                                        dataKey="count"
                                    >
                                        {projectStatusData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: "hsl(var(--card))",
                                            border: "1px solid hsl(var(--border))",
                                            borderRadius: "8px",
                                        }}
                                        formatter={(value, name, props) => [value, t(`status.${props.payload.status}`)]}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="flex flex-wrap justify-center gap-2 mt-4">
                            {projectStatusData.map((item, index) => (
                                <div key={index} className="flex items-center gap-1.5 text-xs">
                                    <div
                                        className="w-2.5 h-2.5 rounded-full"
                                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                    />
                                    <span className="text-muted-foreground">{t(`status.${item.status}`)}</span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Recent Projects & Quick Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Recent Projects */}
                <Card className="lg:col-span-8" data-testid="recent-projects">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="font-heading text-lg">{t("dashboard.recentProjects")}</CardTitle>
                        <Button variant="ghost" size="sm" onClick={() => navigate("/projects")}>
                            {t("common.viewAll")}
                            <ArrowUpRight className="w-4 h-4 ml-1" />
                        </Button>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-4">
                            {recentProjects.length === 0 ? (
                                <p className="text-center text-muted-foreground py-8">{t("projects.noProjects")}</p>
                            ) : (
                                recentProjects.map((project) => (
                                    <div
                                        key={project.project_id}
                                        className="flex items-center justify-between p-4 rounded-lg bg-secondary/50 hover:bg-secondary transition-colors cursor-pointer"
                                        onClick={() => navigate(`/projects/${project.project_id}`)}
                                        data-testid={`project-item-${project.project_id}`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-3">
                                                <h4 className="font-medium truncate">{project.name}</h4>
                                                <Badge className={getStatusColor(project.status)}>
                                                    {t(`status.${project.status}`)}
                                                </Badge>
                                            </div>
                                            {project.client_name && (
                                                <p className="text-sm text-muted-foreground mt-1">{project.client_name}</p>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-4 ml-4">
                                            <div className="w-32">
                                                <div className="flex items-center justify-between text-xs mb-1">
                                                    <span className="text-muted-foreground">{t("projects.progress")}</span>
                                                    <span className="font-mono font-medium">{project.progress}%</span>
                                                </div>
                                                <Progress value={project.progress} className="h-1.5" />
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Quick Stats + Currency Converter */}
                <div className="lg:col-span-4 space-y-6">
                    <Card data-testid="quick-stats">
                        <CardHeader>
                            <CardTitle className="font-heading text-lg">{t("dashboard.recentActivity")}</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-green-100 dark:bg-green-900/30">
                                <CheckCircle2 className="w-5 h-5 text-green-600" />
                                <div>
                                    <p className="text-sm font-medium">{t("projects.tasks")}</p>
                                    <p className="text-lg font-bold font-mono">{stats?.tasks_completed_this_month || 0}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                                <Users className="w-5 h-5 text-blue-600" />
                                <div>
                                    <p className="text-sm font-medium">{t("team.title")}</p>
                                    <p className="text-lg font-bold font-mono">{stats?.team_size || 0}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 p-3 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                                <Clock className="w-5 h-5 text-purple-600" />
                                <div>
                                    <p className="text-sm font-medium">{t("projects.progress")}</p>
                                    <p className="text-lg font-bold font-mono">{stats?.project_completion_rate || 0}%</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <CurrencyConverterWidget />
                </div>
            </div>

            {/* Analytics Section */}
            <AnalyticsSection />
        </div>
    );
};
